import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import axios from 'axios';

import ModalNavigation from '../../../components/modal-navigation';
import { removeModal } from '../../../redux/actions/modal';
import { ButtonThemes } from '../../../enums/button-themes';
import { useDispatch } from 'react-redux';
import { generateUrl } from '../../../helpers/request-builder';
import { IMPRESSION_MODEL_RESOURCE, IMPRESSION_MODEL_CONFIG_RESOURCE } from '../../../configurations/resources-config';

// Layouts
import LayoutImpressionModels from './layouts/layout-impression-models';
import LayoutImpressionModelConfigs from './layouts/layout-impression-model-configs';
import LayoutImpressionDataUpload from './layouts/layout-impression-data-upload';
import WarningMessage from '../../../components/warning-message';

const StyledContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

export type ImpressionModel = {
    id: number;
    value: string;
};

export type ImpressionModelConfig = {
    id: number;
    sourceName: string;
    channelName: string;
    impressionModelId: number;
    refererId: number;
    active: boolean;
    selected: boolean;
};

type ImpressionModelConfigData = {
    id: number;
    active: boolean;
    impression_model: {
        id: number;
        value: string;
    };
    referer: {
        id: number;
        value: string;
        name: string;
    };
};

const LayoutModalManageImpressionModelling = () => {
    const dispatch = useDispatch();
    const [impressionModels, setImpressionModels] = useState<ImpressionModel[]>([]);
    const [impressionModelConfigs, setImpressionModelConfigs] = useState<ImpressionModelConfig[]>([]);
    const [isRefreshingSources, setIsRefreshingSources] = useState(false);
    const [isRefreshingConfigs, setIsRefreshingConfigs] = useState(false);
    const [pageError, setPageError] = useState(false);

    const fetchSources = () =>
        axios({
            method: 'GET',
            url: generateUrl(IMPRESSION_MODEL_RESOURCE.category, IMPRESSION_MODEL_RESOURCE.id, [
                { key: 'limit', value: 0 },
            ]),
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
        }).then(response => {
            setImpressionModels(
                response.data.objects.map((model: ImpressionModel) => ({
                    id: model.id,
                    value: model.value,
                }))
            );
        });

    const fetchConfigs = () =>
        axios({
            method: 'GET',
            url: generateUrl(IMPRESSION_MODEL_CONFIG_RESOURCE.category, IMPRESSION_MODEL_CONFIG_RESOURCE.id, [
                { key: 'limit', value: 0 },
            ]),
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
        }).then(response => {
            setImpressionModelConfigs(
                response.data.objects.map((data: ImpressionModelConfigData) => ({
                    id: data.id,
                    sourceName: data.impression_model.value,
                    channelName: data.referer.value || data.referer.name,
                    impressionModelId: data.impression_model.id,
                    refererId: data.referer.id,
                    active: data.active,
                    selected: false,
                }))
            );
        });

    useEffect(() => {
        handleSourceChange(); // eslint-disable-next-line
    }, []);

    const handleSourceChange = () => {
        setIsRefreshingSources(true);
        setIsRefreshingConfigs(true);
        Promise.all([
            fetchSources().finally(() => setIsRefreshingSources(false)),
            fetchConfigs().finally(() => setIsRefreshingConfigs(false)),
        ]).catch(() => setPageError(true));
    };

    const handleConfigChange = () => {
        setIsRefreshingConfigs(true);
        fetchConfigs()
            .catch(() => setPageError(true))
            .finally(() => setIsRefreshingConfigs(false));
    };

    return (
        <>
            <ModalNavigation
                buttons={[
                    {
                        value: 'close',
                        onClick: () => dispatch(removeModal()),
                        buttonTheme: ButtonThemes.Secondary,
                    },
                ]}
            />
            <h2>Manage Impression Modelling</h2>
            <p>
                Manage your impression modelling configurations, including impression sources, source-channel mappings
                and impression data uploads.
            </p>
            {pageError ? (
                <WarningMessage copy="There was a server issue getting this page ready. Please try again later or contact support@cubed.email." />
            ) : (
                <StyledContainer>
                    <LayoutImpressionModels
                        data={impressionModels}
                        onDelete={handleSourceChange}
                        onEdit={handleSourceChange}
                        isRefreshing={isRefreshingSources}
                    />
                    <LayoutImpressionModelConfigs
                        data={impressionModelConfigs}
                        sources={impressionModels}
                        onDelete={handleConfigChange}
                        onEdit={handleConfigChange}
                        isRefreshing={isRefreshingConfigs}
                    />
                    <LayoutImpressionDataUpload configs={impressionModelConfigs} />
                </StyledContainer>
            )}
        </>
    );
};

export default LayoutModalManageImpressionModelling;
